import { useEffect, useState, useRef } from "react";
import { useResourceContext } from "@/layouts/ResourceContext";
import { Table, Button, message, Spin, Flex, Tabs, Input, List, Tooltip, Space, Badge, Card, Select, App, Breadcrumb, Modal  } from "antd";
import { getServerList, listLogs, download, getFileContent, listFiles, deleteServer } from "@/apis/access";
import { AlignLeftOutlined, CodeOutlined, ReloadOutlined, PoweroffOutlined, DownloadOutlined, EyeOutlined, FileOutlined, HomeOutlined, RightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { WebTerminal } from "./term/WebTerminal";
import { PodSummary } from "./PodSummary";
import { io } from "socket.io-client";
import { CanAccess } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { DeleteConfirmCtrl } from "@/hooks/useResourceDelete";

const { TextArea } = Input;
// the fixed first tab: describes whichever server is selected
const SUMMARY_TAB = "summary";
// green only when running with every container ready; a crash-looping pod is still phase Running
const podBadge = (record) => {
  if (record.status == "Pending") return "error";
  const [ready, total] = (record.ready || "").split("/");
  return record.status == "Running" && ready === total ? "success" : "warning";
};
export const AccessPanel = () => {
  const { state, dispatch } = useResourceContext();
  const [ selectedInstance ] = state.selectedResources;
  
  return selectedInstance?<AccessComp key={selectedInstance.id} id={selectedInstance.id}/>:null;
}
export const ControlPlane = () => {  
  return <AccessComp id="admin"/>;
}
const AccessComp = ({id}) => {
  const { notification  } = App.useApp();
  const { t } = useTranslation();
  const [servers, setServers] = useState([]);
  const [rowKeys, setRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const getServers = async () => {
    setIsLoading(true);
    const response = await getServerList(id);
    setIsLoading(false);
    if (response.status === "OK") {
      let list = response.value;
      if(list.length > 0){
        list = list.map((item) => (item.key = item.name)&&item);
        setServers(list);
        if(rowKeys.length == 0)
            onSelectRow(list[0].key);
      }
      
    } else {
      console.log("get Server failed:" + response);
    }
};
  useEffect(() => {
    getServers();
  }, [id]);


  const columns = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
    }
  ];


  const onSelectRow = (name) => {
    name?setRowKeys([name]):setRowKeys([]);
    setActiveKey(SUMMARY_TAB);
  };

  //tabs
  const getIcon = (type) => {
       return type == "Shell"?<CodeOutlined/>:(type == "Logs"?<AlignLeftOutlined/>:<FileOutlined/>);
  }
  const getTab = (type, namespace, podname) => {
    let pod = servers.find((s) => s.name == podname);
    return type == "Shell"? <WebTerminal name={podname} id={namespace} key={podname}></WebTerminal>:
           (type == "Logs"? <LogTabContent namespace={namespace} podname={podname} containers={pod.containers} key={podname}/>:
            <FSTabContent namespace={namespace} podname={podname} containers={pod.containers} key={podname}/>
           )
  };
  const [activeKey, setActiveKey] = useState(SUMMARY_TAB);
  const [items, setItems] = useState([]);
  const newTabIndex = useRef(0);
  const onChange = (key) => {
    setActiveKey(key);
  };

  const tabMap = useRef({});
  const addTab = (type) => {
    let name = rowKeys[0];
    const newActiveKey = `${name}/${type}`;
    let existed = items.find((n) => n.key == newActiveKey);
    if(existed){
      setActiveKey(existed.key);
    } else {
      setItems([
        ...items,
        {
          label: name,
          children: getTab(type, id, name),
          key: newActiveKey,
          icon: getIcon(type)
        },
      ]);
      setActiveKey(newActiveKey);
      let tabs = tabMap.current[name];
      if(!tabs){
        tabs = [];
        tabMap.current[name] = tabs;
      }
      if(tabs.indexOf(newActiveKey) < 0) tabs.push(newActiveKey);
    }
    
  };
  const remove = (targetKey, tabItems) => {
    const targetIndex = tabItems.findIndex((pane) => pane.key === targetKey);
    const newPanes = tabItems.filter((pane) => pane.key !== targetKey);
    if (newPanes.length && targetKey === activeKey) {
      const { key } = newPanes[targetIndex === newPanes.length ? targetIndex - 1 : targetIndex];
      setActiveKey(key);
    } else if (targetKey === activeKey) {
      setActiveKey(SUMMARY_TAB);
    }
    setItems(newPanes);
    return newPanes;
  };
  const onEdit = (targetKey, action) => {
    if (action === 'remove') {
      remove(targetKey, items);
    }
  };
  const summaryTab = {
    label: "Summary",
    children: <PodSummary namespace={id} podname={rowKeys[0]} refreshKey={refreshKey} key={rowKeys[0]}/>,
    key: SUMMARY_TAB,
    icon: <InfoCircleOutlined/>,
    closable: false
  };
  //tabs end

  const onConfirmDelete = async () => {
    let name = rowKeys[0];
    const response = await deleteServer(id, name);
    if (response.status === "OK") {
      let idx = servers.findIndex((item) => item.key == name);
      if(idx == servers.length-1){
        onSelectRow(idx == 0? null:servers[servers.length-2].key);
      } else {
        onSelectRow(servers[idx+1].key);
      }
      let dp = servers.filter((item) => item.key !== name);
      setServers(dp);
    } else {
      //message.error("Failed to delete :" + response.message);
      notification.error({
        message: "",
        description: "Failed to delete :" + response.message,
      });
    }
    
    let tabs = tabMap.current[name];
    if(tabs){
        let tabItems = items;
        tabs.forEach(key => {
          tabItems = remove(key, tabItems);
        });
        delete tabMap.current[name];
    }

  };
  
  return (
    <Flex vertical={false} style={{width:"100%", height:"100%"}} gap={3}>
        <List>
          <Flex justify="space-between" align="center" >
            <span>Servers:</span>
            <span>
            <Space.Compact block>
              <CanAccess key="access" resource="instances" action="access">      
                <Tooltip title="Shell">
                  <Button icon={getIcon("Shell")} onClick={() => {addTab("Shell");}} disabled={isLoading}/>
                </Tooltip>
              </CanAccess>
              <Tooltip title="Logs">
                <Button icon={getIcon("Logs")} onClick={() => {addTab("Logs");}} disabled={isLoading}/>
              </Tooltip>
              <CanAccess key="access" resource="instances" action="access">   
                <Tooltip title="Files">
                  <Button icon={getIcon("Files")} onClick={() => {addTab("Files");}} disabled={isLoading}/>
                </Tooltip>
              </CanAccess> 
              <CanAccess key="access" resource="instances" action="access">                  
                <Tooltip title="Restart">
                  <Button icon={<PoweroffOutlined />} disabled={isLoading||rowKeys.length<1}
                    onClick={() => {
                      
                        const dialog = Modal.confirm({
                          title: t(`access.confirm.delete.title`),
                          content: <DeleteConfirmCtrl content={t(`access.confirm.delete.content`, { id:rowKeys[0] })} id={rowKeys[0]}
                          setConfirmed={(bool) => dialog.update({okButtonProps:{ disabled: !bool }})}/>,
                          onOk: () => {
                            onConfirmDelete();
                          }
                        });
                      }
                    }                   
                  />
                </Tooltip>
              </CanAccess>  
              <Tooltip title="Refresh">
                  <Button icon={<ReloadOutlined />} onClick={() => {getServers(); setRefreshKey((k) => k + 1);}} disabled={isLoading}/>
              </Tooltip>                          
            </Space.Compact></span>
          </Flex>
          <Table dataSource={servers} bordered={false} loading={isLoading} showHeader={false} style={{width:"250px"}}
                 columns={columns} 
                 size="small"
                 rowSelection={{
                  hideSelectAll: true,
                  type: "radio",
                  selectedRowKeys:rowKeys,
                  renderCell:(value, record, index, originNode) => <Badge status={podBadge(record)}/>,
                  onSelect: (record, selected) => {
                    if (selected) {
                      onSelectRow(record.name);
                    }
                  },
                }}
                 onRow={(record) => ({
                  onClick: () => onSelectRow(record.name),
                })}/>
        </List>
          <Flex vertical gap={10} style={{width:"calc(100% - 250px)"}}>
            <Tabs
              hideAdd
              onChange={onChange}
              activeKey={activeKey}
              type="editable-card"
              onEdit={onEdit}
              items={[summaryTab, ...items]}
              size="small"
              tabBarStyle={{marginBottom:"0px"}}
            />
          </Flex>  
            
        
      </Flex>
  );
};

const TabContent = ({idx, type}) => {
  return (<div>{type}</div>);
};

const LogTabContent = ({namespace, podname, containers}) => {
  const container = useRef(containers[0]);
  const logs = useRef([]);
  const [selectedLog, setSelectedLog] = useState();
  const [selectedLogValue, setSelectedLogValue] = useState();
  const [logOptions, setLogOptions] = useState([]);
  const socket = useRef(null);
  const key = useRef(Math.random().toString(36).substr(2, 9));
  const messages = useRef([]);
  const [ongoingLog, setOngoingLog] = useState("");
  const textRef = useRef(null);
  const isStreaming = useRef(true);
  useEffect(() => {
    const getLogs = async () => {
        setSelectedLog({"log":""});
        logs.current = [{"log":""}];
        const response = await listLogs(namespace, podname, container.current);
        if (response.status === "OK") {
          let list = response.value;
          logs.current = [{"log":""}].concat(list);
          
        } else {
          console.log("get Server failed:" + response);
        }
        
        let options = [];
        logs.current.forEach((item) => {
            options.push({"label":item.log?(item.log + (item.size?`(${item.size})`:"")):"-- Container Log --", "value":item.log});
        }); 
        setLogOptions(options);  
        onChangeLog(logs.current[0].log);
    };

    getLogs();

    return () => {
      if(socket.current){
        socket.current.disconnect();
      }
    };
  }, [namespace, podname]);
  const onChangeContainer = (value) =>{
    //todo, refresh logs
  };

  const onChangeLog = (value) => {
      setSelectedLogValue(value);
      let item = logs.current.find((i) => i.log == value);
      setSelectedLog(item);
      if(item){
        if(item.path){
          isStreaming.current = false;
          getSetLogText(item.path);
        } else {
          isStreaming.current = true;
          setStreamingLog();
          getStreamingLog();
        }
      } 
  };

  const getSetLogText = async (path) => {
    setOngoingLog("");
    const response = await getFileContent(namespace, podname, container.current, path);
    if (response.status === "OK") {
      let content = response.value;
      setOngoingLog(content);
      
    } else {
      console.log("get Server failed:" + response);
    }
    
  };

  const getStreamingLog =  () => {

      socket.current = io(window.location.origin,{
        "path":"/api/v1/provision/socket.io",
        "query": `type=podlog&pod_name=${podname}&namespace=${namespace}&container=${container.current}&key=${key.current}`,
        "transports": ["websocket"]
      });

      socket.current.on("connect_error", (err) => {
        // the reason of the error, for example "xhr poll error"
        console.log(">>> connect with " + err.message);
      
        // some additional description, for example the status code of the initial HTTP response
        console.log(">>>" + err.description);
      
        // some additional context, for example the XMLHttpRequest object
        console.log(">>>" + err.context);
      });

      socket.current.on(key.current, function(msg) {
        if(messages.current.length ==5000){//show latest 5000 lines
          messages.current.shift();
        }
        let data = msg.data;
        if(data.indexOf("\u001b[0m\u001b[0m") == 0){
          data = data.substring("\u001b[0m\u001b[0m".length);//trim unexpected chars
        }
        if(data.indexOf("\u001b[0m\u001b[33m") == 0){
          data = data.substring("\u001b[0m\u001b[33m".length);//trim unexpected chars
        }
         if(data.indexOf("\u001b[0m\u001b[31m") == 0){
          data = data.substring("\u001b[0m\u001b[31m".length);//trim unexpected chars
        }       
        messages.current.push(data);
        if(isStreaming.current){
          setStreamingLog();
        }
      });
  };

  const setStreamingLog = () => {
    const textarea = textRef.current.resizableTextArea.textArea;
    let diff = textarea.scrollHeight - textarea.scrollTop - textarea.offsetHeight;          
    setOngoingLog(messages.current.join("\n"));
    if(diff < 10) {
      setTimeout(() => {
        textarea.scrollTop = textarea.scrollHeight;
      }, 200);        
    }
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const {doDownload} = useDownload();
  const setDownloadState = (path, downloading) => {
    setIsDownloading(downloading);
  };
  const onDownload = () => {
    let name = selectedLog.log;
    if(!name) name = podname + "_container.log";
    doDownload(namespace, podname, container.current, name, selectedLog.path, setDownloadState, ".zip");
  };

  return (
    <Card title={(
      <>
      <Space size="large">
        <Space size="small">
            Container
            <Select size="small" options={containers?.map((x) =>  x&&{"label":x, "value":x})} onChange={onChangeContainer} defaultValue={container.current}/>
        </Space>
        <Space size="small">
            Logs
            <Space.Compact block size="small">
              <Select options={logOptions} onChange={onChangeLog} value={selectedLogValue} style={{width: 360,}}/>
              <Tooltip title="Download">
                <Button type="primary" icon={<DownloadOutlined />} onClick={onDownload}/>
              </Tooltip>
              
            </Space.Compact>
            {isDownloading && <Spin size="small" />}
        </Space>
      </Space>
      </>
    )
    } style={{width:"100%", height:"100%"}}>
        <TextArea value={ongoingLog} readOnly rows="25"
          style={{width:"100%",height:"100%", resize:"none"}} ref={textRef}/>
    </Card>
  );

};

const useDownload = () => {
  const { notification  } = App.useApp();  
  const doDownload = async (namespace, podname, container, name, path, setDownloading, fileType) => {
     
    try{
      console.log(">>>>>Start to download");
      setDownloading(path, true);
      const response = await download(namespace, podname, container, path, name);
      console.log(">>>>>Get download response");
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileType?(name+fileType):name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloading(path, false);
    } catch(error) {
      notification.error({
        message: '',
        description:
          `Failed to download ${name}. ${error}`,
          placement: 'topRight'
      });
    }
  };

  return {doDownload};
}



const FSTabContent = ({namespace, podname, containers}) => {
  const container = useRef(containers[0]);
  const [fsData, setFSData] = useState([]);
  const initFSItem = [{title:(<HomeOutlined />), key:"/", path:""}];
  const fSItemsRef = useRef(initFSItem.concat());
  const [fsItems, setFSItems] = useState(initFSItem.concat());
  const [isLoadingFS, setIsLoadingFS] = useState(false);
  const [favoriteOpts, setFavoriteOpts] = useState([]);
  const [favoriate, setFavoriate] = useState("");
  const [tableKey, setTableKey] = useState("");
  const fsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value, record) => record.type == "folder"? (<a onClick={(e) => drillInFS(value, e)}>{value}</a>):
                                                          (<li>{value}</li>)
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 60,
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 100
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 60,
      render: (value, record) => 
        (record.type == "file" && record.size != "0" && <>
        <Tooltip title="View content"><EyeOutlined onClick={(e) => viewFile(record.name)}/></Tooltip>
        <Tooltip title="Download"><DownloadOutlined onClick={(e) => downloadFile(record.name)}/></Tooltip>
        {fSItemsRef.current.length > 0 && isDownloadingMap[fSItemsRef.current.at(-1).path + "/" + record.name] && <Spin size="small" />}</>) 
    }
  ];

  
  const drillInFS = (name, e) => {
    e.preventDefault();
    let items = [];
    let last = fSItemsRef.current.pop();
    if(fSItemsRef.current.length > 0){       
        last.title = (<a onClick={() => backToFS(last.path)}>{last.key}</a>);       
    } else {
        last.title = (<a onClick={() => backToFS("/")}><HomeOutlined /></a>);
    }
    items.push(last);
    let path = last.path + "/" + name;
    items.push({title:name, key:name, path:path});
    fSItemsRef.current = fSItemsRef.current.concat(items);
    setFSItems(fSItemsRef.current);
    getDetail(path);
    setFavoriate("");
  };

  const backToFS = (path) => {
    let idx = fSItemsRef.current.findIndex((item) => item.path == path);
    let items = fSItemsRef.current.slice(0, idx + 1);

    let last = items[idx];   
    if(idx > 0){
      last.title = last.key;
      getDetail(path);
    } else {
      last.title = (<HomeOutlined />);
      getDetail("/");
    }
    fSItemsRef.current = items;
    setFSItems(items);
    setFavoriate("");
  };

  const getDetail = async (path) =>{
    setIsLoadingFS(true);
    const response = await listFiles(namespace, podname, container.current, path);
    setIsLoadingFS(false);
    if (response.status === "OK") {
      let value = response.value;
      let filelist = value.files || [];
      filelist.sort((a,b) => {
        let a1 = (a.type == "folder"?1:0);
        let b1 = (b.type == "folder"?1:0);
        return b1- a1;
      });
      setFSData(filelist);
      setTableKey(path);
      if(value.folder){
        updateNavigator(value.folder);
      }
      if(value.favorite && value.favorite.length > 0){
         let opts = [];
         value.favorite.forEach((favorite) => {
          let opt = {label:favorite.alias, value:favorite.path, title:favorite.path};
          opts.push(opt);
         })
         setFavoriteOpts(opts);
      }
      
    } else {
      setFSData([]);
    }
  };

  const updateNavigator = (path) => {
    if(path.startsWith("/")){
      path = path.substring(1);
    }
    let pathElem = path.split("/");
    let items = [{key:"", path:"/"}];
    if(path == "/"){
      items[0].title = (<HomeOutlined />);
    } else {
      items[0].title = (<a onClick={() => backToFS("/")}><HomeOutlined /></a>);
    
      for(let i = 0; i < pathElem.length; i++){
        let p = "/" + pathElem.slice(0, i+1).join("/");
        let item = {key:pathElem[i], path:p};          
        if(i == pathElem.length - 1){
          item.title = item.key;
        } else {
          item.title = (<a onClick={() => backToFS(p)}>{pathElem[i]}</a>);
        }
        items.push(item);
          
      }     
    }
    fSItemsRef.current = items;        
    setFSItems(items);
  };

  useEffect(() => {    
    setFSItems(initFSItem.concat());
    getDetail("");
  }, [namespace, podname]);

  const {doDownload} = useDownload();
  const [isDownloadingMap, setIsDownloadingMap] = useState({});

  const setDownloadState = (path, downloading) => {
    let map = {};
    Object.assign(map, isDownloadingMap);
    map[path] = downloading;
    setIsDownloadingMap(map);
  };
  const downloadFile = (name) => {
    let i = fSItemsRef.current.length - 1;
    let path = fSItemsRef.current[i].path + "/" + name;
    doDownload(namespace, podname, container.current, name, path, setDownloadState,".zip");
  };
  const viewFile = (name) => {
    let i = fSItemsRef.current.length - 1;
    let path = fSItemsRef.current[i].path + "/" + name;
    setIsOpen(true);
    doGetContent(path);
  };

  const [log, setLog] = useState("");
  const [filePath, setFilePath] = useState("");
  const doGetContent = async (path) => {
    setFilePath(path);
    setLog("");
    const response = await getFileContent(namespace, podname, container.current, path);
    if (response.status === "OK") {
      let content = response.value;
      setLog(content);
      
    } else {
      console.log(`Failed to get file content of ${path} : ${response}`);
    }
    
  };

  const onChangeFavorite = (value) => {
    setFavoriate(value);
    if(value){
      updateNavigator(value);
      getDetail(value);
    }   
  };

  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => {
      setIsOpen(false);
  };

  return (
    <div>
      <Flex style={{width: '100%'}} justify='space-between' align='center'>
        <Breadcrumb items={fsItems} separator="/"/>
        <Space size="small">
          Favorite
          <Select options={favoriteOpts} onChange={onChangeFavorite} defaultValue="" value={favoriate} style={{width: 150,}}/>
        </Space>
      </Flex>
      <Table size="small" key={tableKey} dataSource={fsData} columns= {fsColumns} loading={isLoadingFS}/>
      <Modal
        title={`Content of '${filePath}'`}
        centered
        footer={
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        }
        open={isOpen}
        onCancel={onClose}
        maskClosable = {false}
        width={800}
        height={600}
      >
        <TextArea value={log} readOnly rows="25"
          style={{width:"100%",height:"100%", resize:"none"}}/>
      </Modal>
    </div>
  );
};

